/**
 * Feature E02-F06 — "Event-лог / таймлайн проекта" request/response contracts.
 *
 * 1:1 port of the legacy n8n `project_events` writes and the get_1_project read
 * (docs/source-n8n/{sql-queries.md → "DB: Get 1 Project + all data" events
 * sub-select; reference/{Create,AI,Kickoff,Email}.md → the INSERT INTO
 * project_events that lives inside every mutation CTE).
 *
 * Single source of truth for:
 *   - GET /projects/:id/events     timeline read (T03 UI consumer)
 *   - the canonical event_type set + payload shapes (T01 logEvent utility)
 *
 * IDOR invariant (carried over unchanged): `created_by` is ALWAYS the session
 * identity passed in by the api side, NEVER a body field. It is intentionally
 * absent from any *input* schema here so it can never be trusted from a request.
 *
 * Lives in its OWN file (not re-exported from the shared barrel by this agent);
 * the serial Wire pass adds the `export *` line to `packages/shared/src/index.ts`.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// T01 — canonical event_type set
// ---------------------------------------------------------------------------
/**
 * The real `event_type` values written by the legacy stack (per the n8n
 * reference: Create/AI/Kickoff/Email branches). The task spec names the
 * normalized core five; the extra ones are the literal legacy strings kept so
 * the timeline can humanize every row that already exists in production.
 *
 *   project_created          — project created (from email or manual);
 *                              payload {source?, emailId?} | {email_id}
 *   ai_analysis_completed    — pre_kickoff_analysis saved;
 *                              payload {classification, recommendation}
 *   pre_kickoff_email_sent   — pre-kickoff draft sent;  payload {option}
 *   email_linked             — inbound email linked to project;
 *                              payload {emailId, summary?, action_items?}
 *   email_unlinked           — email detached;  payload {emailId}
 *
 * Legacy literals also recognized for display parity:
 *   project_created_from_email, project_created_manual, project_updated,
 *   kickoff_enrichment_completed, stage5_email_sent, final_package_sent,
 *   client_followup_email_sent, sourcing_sheet_email_sent,
 *   internal_notification_sent.
 */
export const PROJECT_EVENT_TYPES = [
    'project_created',
    'ai_analysis_completed',
    'pre_kickoff_email_sent',
    'email_linked',
    'email_unlinked',
];
/**
 * All `event_type` strings the timeline knows how to render. Free-form: the
 * column is `varchar(100)` and the legacy stack writes several literals beyond
 * the core five, so the contract accepts ANY non-empty string and the humanizer
 * falls back gracefully (T03 acceptance: "человекочитаемое описание payload").
 */
export const projectEventTypeSchema = z.string().min(1).max(100);
// ---------------------------------------------------------------------------
// T01 — payload shapes per event_type (jsonb, free-form)
// ---------------------------------------------------------------------------
/** `payload` is an arbitrary jsonb object (T01 acceptance). */
export const projectEventPayloadSchema = z.record(z.string(), z.unknown());
// ---------------------------------------------------------------------------
// T03 — timeline read DTO (get_1_project events sub-select shape)
// ---------------------------------------------------------------------------
/**
 * One timeline row, mirroring the legacy events sub-select
 * (`SELECT id, project_id, event_type, payload, created_at FROM project_events
 * ORDER BY created_at DESC LIMIT 50`) plus `created_by` (nullable for system
 * events) and a server-computed human-readable `description` for the UI.
 */
export const projectEventSchema = z.object({
    id: z.number().int(),
    project_id: z.number().int(),
    event_type: projectEventTypeSchema,
    payload: projectEventPayloadSchema,
    /** Session user id of the actor; null for system/automatic events. */
    created_by: z.string().nullable(),
    /** ISO 8601 timestamp. */
    created_at: z.string(),
    /** Server-rendered RU human-readable summary of (event_type + payload). */
    description: z.string(),
});
/** Hard cap matching the legacy `LIMIT 50` events sub-select. */
export const PROJECT_EVENTS_LIMIT = 50;
/** GET /projects/:id/events response — newest-first, ≤50 rows. */
export const projectEventsResponseSchema = z.object({
    success: z.literal(true).optional(), // web client strips `success`; backend still sends it
    events: z.array(projectEventSchema),
});
//# sourceMappingURL=project-events.js.map